import { scopeRulesOf, type OverlayScope } from './scope';

/**
 * 浮层堆栈。
 *
 * 必须是**模块级单例**，不能是每个浮层自己的 `useState`：
 *   - 「Esc 只关最上面那层」需要全局知道谁是顶层；
 *   - 「同时只由顶层持有滚动锁」也需要全局计数；
 *   - 多层浮层可能来自树上互不相邻的两处（地图上一个气泡、标题栏一个确认框）。
 *
 * 与 `themeStore` 同样的取舍：为几十行状态引一个状态库不值得，`useSyncExternalStore`
 * 直接消费这个最小外部 store 就够了。
 */

export interface OverlayLayer {
  readonly id: string;
  readonly scope: OverlayScope;
  /** 无障碍名称，仅用于调试与"当前有哪些浮层"这类展示 */
  readonly label: string;
  /** 打开这一层时正在聚焦的元素（通常就是触发按钮）：关闭后要把焦点还回去 */
  readonly restoreFocus: HTMLElement | null;
  /** 请求关闭。它只表达"用户想关"，是否真的关由调用方（受控组件）决定 */
  readonly close: () => void;
}

const EMPTY_LAYERS: readonly OverlayLayer[] = [];
let layers: readonly OverlayLayer[] = EMPTY_LAYERS;
const listeners = new Set<() => void>();

/** 进入锁定前的 `body.style.overflow`，用来原样还原（应用可能自己有 `hidden`） */
let savedBodyOverflow: string | null = null;
let escapeListenerInstalled = false;

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeOverlayStack(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** 供 `useSyncExternalStore` 用：只在真正增删时替换数组，引用保持稳定 */
export function getOverlayStack(): readonly OverlayLayer[] {
  return layers;
}

export function pushOverlayLayer(layer: OverlayLayer): void {
  layers = [...layers, layer];
  syncGlobals();
  emit();
}

export function removeOverlayLayer(id: string): void {
  const next = layers.filter((layer) => layer.id !== id);
  if (next.length === layers.length) return;
  layers = next;
  syncGlobals();
  emit();
}

export function topOverlayLayer(): OverlayLayer | undefined {
  return layers[layers.length - 1];
}

export function isTopOverlayLayer(id: string): boolean {
  return topOverlayLayer()?.id === id;
}

/** 关闭最上面那层；返回是否真的发出了关闭请求（没有浮层时为 false） */
export function closeTopOverlayLayer(): boolean {
  const top = topOverlayLayer();
  if (top === undefined) return false;
  top.close();
  return true;
}

/** 请求关闭所有浮层（例如链路断开、切机）。是否真的关由各调用方决定 */
export function closeAllOverlayLayers(): void {
  // 先复制一份再遍历：`close()` 通常是 setState，会在遍历过程中改到栈本身
  for (const layer of [...layers]) layer.close();
}

/**
 * 强制清空堆栈。
 *
 * 只用于测试隔离与"整个界面重建"这类场景 —— 它**不会**改各组件的 `open` 状态，
 * 因此组件仍然认为自己开着（只是不再参与堆栈）。正常关闭请用 `closeAllOverlayLayers`。
 */
export function resetOverlayStack(): void {
  layers = EMPTY_LAYERS;
  syncGlobals();
  emit();
}

function handleEscape(event: KeyboardEvent): void {
  if (event.key !== 'Escape') return;
  const top = topOverlayLayer();
  if (top === undefined) return;
  if (!scopeRulesOf(top.scope).dismissOnEscape) return;
  // 只处理最上层，且不 stopPropagation：底层若有自己的 Esc 语义（例如退出编辑模式），
  // 由它自行用 isTopOverlayLayer 判断；这里的承诺只有一条 —— Esc 关掉最上面那层。
  top.close();
}

function syncEscapeListener(): void {
  if (typeof document === 'undefined') return;
  const shouldInstall = layers.length > 0;
  if (shouldInstall && !escapeListenerInstalled) {
    document.addEventListener('keydown', handleEscape);
    escapeListenerInstalled = true;
  } else if (!shouldInstall && escapeListenerInstalled) {
    document.removeEventListener('keydown', handleEscape);
    escapeListenerInstalled = false;
  }
}

/**
 * 滚动锁定。
 *
 * 只由 `blocking` 触发：realtime 的浮层不许挡住飞行员继续操作底下的界面，
 * 锁滚动就等于把人钉在浮层这一屏上。
 */
function syncScrollLock(): void {
  if (typeof document === 'undefined') return;
  const needed = layers.some((layer) => scopeRulesOf(layer.scope).lockScroll);
  if (needed && savedBodyOverflow === null) {
    savedBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  } else if (!needed && savedBodyOverflow !== null) {
    document.body.style.overflow = savedBodyOverflow;
    savedBodyOverflow = null;
  }
}

function syncGlobals(): void {
  syncEscapeListener();
  syncScrollLock();
}
